<script lang="ts">
	import Card from '@smui/card';
	import Select, { Option } from '@smui/select';
	import Input from '@smui/textfield';
	import { _ } from 'svelte-i18n';
	import FieldLoading from '../../../../../../shared/components/common/Loading/FieldLoading.svelte';
	import TableLoading from '../../../../../../shared/components/common/Loading/TableLoading.svelte';
	import ConfirmModal from '../../../../../../shared/components/common/Modal/ConfirmModal.svelte';
	import Pagination from '../../../../../../shared/components/common/Table/Pagination.svelte';
	import Title from '../../../../../../shared/components/common/Title.svelte';
	import { formatDate, resetPageIfFiltersChanged } from '../../../../../../shared/functions';
	import FiltroGenerico from '../../../../../../shared/models/Filtri/FiltroGenerico';
	import PSCLElaborazione from '../../../../../../shared/models/PSCL/Elaborazione/PSCLElaborazione';
	import Sede from '../../../../../../shared/models/Sede/Sede';
	import { hasRoleSysAdmin } from '../../../../../../shared/stores';

	let filters: FiltroGenerico = {
		sedeId: 0,
		descrizione: '',
		annoInserimento: new Date().getFullYear(),
		pagina: 1,
		elementiPerPagina: 10,
	};

	$: [data, loading, error] = PSCLElaborazione.getList(filters);
	let [dataSedi, loadingSedi, errorSedi] = Sede.getList();

	let [dataEliminazione, loadingEliminazione, errorEliminazione] = [null, null, null];
	let openConfirmDeleteDialog: boolean = false;
	let idToDelete: number = 0;

	async function onDelete(id) {
		[dataEliminazione, loadingEliminazione, errorEliminazione] = await PSCLElaborazione.delete(id);
		if (!$errorEliminazione) {
			[data, loading, error] = PSCLElaborazione.getList(filters);
		}
	}

	let prevFilters = {};

	$: {
		const newPage = resetPageIfFiltersChanged(prevFilters, filters);
		if (newPage !== null) {
			filters.pagina = newPage;
		}
		prevFilters = { ...filters };
	}

	const currentYear = new Date().getFullYear();

	$: {
		if (filters.annoInserimento === null) {
			delete filters.annoInserimento;
		} else {
			filters.annoInserimento = Number(filters.annoInserimento);
		}
	}

	function getYears(): number[] {
		const startYear = 2020;
		return Array.from({length: currentYear - startYear + 1}, (_, i) => currentYear - i);
	}
</script>

<ConfirmModal bind:showModal="{openConfirmDeleteDialog}" onConfirm="{() => onDelete(idToDelete)}" />
<Card class="md:px-8 md:py-8" padded>
	<div class="flex justify-between">
		<Title title="{$_('page.pscl.list.title')}  {currentYear}" />
		<div class="sm:flex-nowrap sm:space-x-4 flex flex-wrap">
			<a href="/#/psclelaborazione/0">
				<button class="btn-white h-14 w-14 sm:h-16 sm:w-16">
					<span class="material-icons-outlined text-2xl leading-[3rem] sm:text-3xl sm:leading-[3.25rem]">add</span>
				</button>
			</a>
		</div>
	</div>
	<div class="md:grid-cols-2 lg:grid-cols-3 grid grid-cols-1 gap-8 p-4">
		<Input label="{$_('page.pscl.list.description')}" bind:value="{filters.descrizione}" />
		{#if $hasRoleSysAdmin}
			{#if $loadingSedi}
				<FieldLoading />
			{:else}
				<Select key="{(sede) => `${(sede && sede.id) || ''}`}" label="{$_('page.pscl.list.location')}">
					{#if $dataSedi.response}
						<Option on:click="{() => (filters.sedeId = 0)}" />
						{#each $dataSedi.response as sede (sede.id)}
							{#if sede.denominazione}
								<Option value="{sede}" on:click="{() => (filters.sedeId = sede.id)}">{sede.denominazione}</Option>
							{/if}
						{/each}
					{/if}
				</Select>
			{/if}
		{/if}
		<Select 
			label="Anno inserimento" 
			bind:value="{filters.annoInserimento}"
			class="filled-select"
			>
			<Option value={null}>Tutti</Option>
			{#each getYears() as year}
				<Option value="{year}">{year}</Option>
			{/each}
		</Select>
	</div>
	{#if $loading}
		<TableLoading />
	{:else}
		<div class="flex flex-col">
			<div class="sm:-mx-6 lg:-mx-8 -my-2 overflow-x-auto">
				<div class="sm:px-6 lg:px-8 inline-block min-w-full py-2 align-middle">
					<div class="overflow-hidden">
						<table class="min-w-full p-0 m-0 text-sm leading-10 border-t-4 border-collapse border-black table-auto">
							<thead>
								<tr class="p-1">
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.pscl.list.description')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('page.pscl.list.location')}</th
									>
									<th scope="col" class="border-b p-2.5 text-center text-xs leading-6 tracking-wider">Data inserimento</th>
									<th scope="col" class="relative border-b p-2.5 text-center text-xs leading-6 tracking-wider"
										>{$_('global.actions')}</th
									>
								</tr>
							</thead>
							<tbody>
								{#each $data.response as row}
									<tr class="odd:bg-white even:bg-gray-50 p-1">
										<td class="border-b p-2.5 text-center leading-5">{row.descrizione ?? ''}</td>
										<td class="border-b p-2.5 text-center leading-5">{row.sede.denominazione ?? ''}</td>
										<td class="border-b p-2.5 text-center leading-5">{formatDate(row.dataInsert, 'dd/mm/yyyy')}</td>
										<td class="whitespace-nowrap border-b p-2.5 text-center leading-5">
											<div class="item-center flex justify-center">
												<div>
													<a href="/#/psclelaborazione/{row.id}">
														<button class="h-7.5 w-7.5">
															<span class="material-icons-outlined text-base leading-[1.8rem]">launch</span>
														</button>
													</a>
												</div>
												<div class="ml-4 h-7.5 w-7.5">
													<button
														on:click="{() => {
															idToDelete = row.id;
															openConfirmDeleteDialog = true;
														}}"
														class="h-7.5 w-7.5"
													>
														<span class="material-icons-outlined text-base leading-[1.8rem]">delete</span>
													</button>
												</div>
											</div>
										</td>
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
		<div class="right-0">
			<Pagination bind:current="{filters.pagina}" bind:num_pages="{$data.totalPages}" bind:per_page="{filters.elementiPerPagina}" />
		</div>
	{/if}
</Card>
